import { useAtomValue } from "jotai";
import first from "lodash/first";
import mapValues from "lodash/mapValues";
import pick from "lodash/pick";
import { useMemo } from "react";
import { graphql, useFragment } from "react-relay";
import { findFirstNonEmptyString } from "scmp-sdk";

import { paywall } from "shared/data";
import { notEmpty } from "shared/lib/utils";

import type {
  Sentiment,
  SentimentTargeting,
  Targeting,
} from "scmp-app/components/advertisement/ad-slots/types";
import {
  sentimentTargetingKeys,
  TargetingPaidType,
} from "scmp-app/components/advertisement/ad-slots/types";
import { checkIsMainTopic } from "scmp-app/components/content/helpers";
import { rosettaAtom } from "scmp-app/lib/rosetta";
import { useRosettaCampaign } from "scmp-app/lib/rosetta/hooks/rosetta-campaign";
import { usePaywallStrategies } from "scmp-app/lib/rosetta/hooks/rosetta-paywall-strategies";
import type { articleBaseAdvertisingInfoArticle$key } from "scmp-app/queries/__generated__/articleBaseAdvertisingInfoArticle.graphql";

import { checkArticleIsInArchiveWall } from "./piano-integration/helpers";

export const useArticleBaseAdvertisingInfo = (article_: articleBaseAdvertisingInfoArticle$key) => {
  const article = useFragment(
    graphql`
      fragment articleBaseAdvertisingInfoArticle on Article {
        advertZone(version: 2)
        entityId
        sentiment
        contentLock
        topics {
          entityId
          sponsor {
            __typename
          }
        }
        types {
          value {
            entityId
          }
        }
        sections {
          value {
            entityId
          }
        }
        paywallTypes {
          entityId
        }
        ...helpersIsArchiveWallArticle
        ...helpersCheckIsMainTopicContent
      }
    `,
    article_,
  );
  const asyncRosettaState = useAtomValue(rosettaAtom);
  const { getActiveCampaignSettings } = useRosettaCampaign();
  const { getActivePaywallStrategy } = usePaywallStrategies();

  const articleBaseAdvertisingInfo = useMemo<{
    targeting: Targeting;
    zone?: string;
  }>(() => {
    const mainTopic = article.topics?.find(topic => checkIsMainTopic(article, topic?.entityId));
    const computeSentimentTargeting = () => {
      const sentiment = article.sentiment as null | Sentiment;
      if (sentiment === null) return {};
      const filtered = pick(sentiment, sentimentTargetingKeys);
      const mapped: SentimentTargeting = mapValues(filtered, value => value?.toString());
      return mapped;
    };

    const isVelocityWall = (() => {
      const paywallTypesIds = article.paywallTypes?.map(type => type?.entityId) ?? [];

      const hasNotVelocityWallId = paywallTypesIds.includes(paywall.notVelocity.entityId);
      if (hasNotVelocityWallId) return false;

      const velocityWallIds = [
        paywall.velocity.entityId,
        paywall.velocity2kViews.entityId,
        paywall.velocity3kViews.entityId,
      ];

      return velocityWallIds.some(id => paywallTypesIds.includes(id));
    })();

    const appSettings = asyncRosettaState?.result?.appSettings ?? {};
    const { oldArticleTimeframe = 5 } = appSettings;

    const activeCampaignSettings = getActiveCampaignSettings();
    const activeArchivePaywallStrategy = getActivePaywallStrategy({ paywallType: "archive" });

    // NOTE: Need to update to include Lite as part of archive?
    const isInArchiveWall = checkArticleIsInArchiveWall(
      article,
      activeCampaignSettings?.settings?.paywall?.archive?.articleAgeThresholdDays ??
        activeArchivePaywallStrategy?.settings?.paywall?.archive?.articleAgeThresholdDays ??
        oldArticleTimeframe,
    );

    return {
      targeting: {
        articletype:
          first(article.types?.filter(notEmpty))
            ?.value?.filter(notEmpty)
            .map(type => type?.entityId) ?? "DEFAULT",
        paid: (() => {
          if (mainTopic?.sponsor) {
            return TargetingPaidType.Sponsored;
          } else if (!article.contentLock) {
            return TargetingPaidType.Free;
          } else if (isVelocityWall) {
            return TargetingPaidType.PaidVelocity;
          } else if (isInArchiveWall) {
            return TargetingPaidType.PaidArchive;
          } else if (article.contentLock) {
            return TargetingPaidType.Paid;
          } else {
            return TargetingPaidType.Free;
          }
        })(),
        scnid: article.entityId,
        scsid: first(article.sections?.filter(notEmpty))
          ?.value?.filter(notEmpty)
          .map(section => section.entityId),
        sctid: mainTopic?.entityId,
        ...computeSentimentTargeting(),
      },
      zone: findFirstNonEmptyString(article.advertZone, "default"),
    };
  }, [
    article,
    asyncRosettaState?.result?.appSettings,
    getActiveCampaignSettings,
    getActivePaywallStrategy,
  ]);

  return articleBaseAdvertisingInfo;
};
